import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import SlidingDrawer from '../../components/SlidingDrawer';

const FinanceManager = () => {
    const [dues, setDues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
    const [selectedDue, setSelectedDue] = useState(null);
    const [formData, setFormData] = useState({
        memberId: '',
        amount: '',
        method: 'CASH',
        ref: ''
    });

    const ICONS = {
        rupee: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12"></path><path d="M6 8h12"></path><path d="m6 13 8.5 8"></path><path d="M6 13h3"></path><path d="M9 13c6.667 0 6.667-10 0-10"></path></svg>,
        ledger: <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-0.5-5"></path><path d="M8 7h6"></path><path d="M8 11h8"></path><path d="M8 15h6"></path></svg>,
        add: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
    };

    useEffect(() => {
        fetchDues();
    }, []);

    const fetchDues = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/finance/dues`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDues(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const confirm = await Swal.fire({
            title: 'Confirm Governance Audit',
            text: `You are about to record ₹${Number(formData.amount).toLocaleString()} for ${formData.memberId}. This action is logged for audit purposes.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#C0722C',
            confirmButtonText: 'Finalize Record',
            background: '#FDF8F3'
        });

        if (confirm.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${API_BASE_URL}/api/finance/pay`, {
                    memberId: formData.memberId,
                    amount: Number(formData.amount),
                    paymentMethod: formData.method,
                    referenceNumber: formData.ref,
                    description: 'Governance Manual Entry'
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                Swal.fire({
                    title: 'Protocol Success',
                    text: 'Payment recorded and ledger updated.',
                    icon: 'success',
                    confirmButtonColor: '#2C1A10'
                });

                setIsDrawerOpen(false);
                setFormData({ memberId: '', amount: '', method: 'CASH', ref: '' });
                fetchDues();
            } catch (err) {
                Swal.fire('Conflict Detected', 'Payment recording failed. Please check Member ID.', 'error');
            }
        }
    };

    const InputField = ({ label, value, onChange, placeholder, type = "text", required = false, icon }) => (
        <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>
                {label} {required && <span style={{ color: '#C0722C' }}>*</span>}
            </label>
            <div style={{ position: 'relative' }}>
                {icon && <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', display: 'flex', pointerEvents: 'none' }}>{icon}</span>}
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    required={required}
                    style={{
                        width: '100%',
                        padding: `14px 16px 14px ${icon ? '48px' : '16px'}`,
                        borderRadius: '12px',
                        border: '1px solid #E0E0E0',
                        fontSize: '14px',
                        color: '#2C1A10',
                        backgroundColor: '#F9F9F9',
                        boxSizing: 'border-box',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#C0722C'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 4px rgba(192, 114, 44, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#E0E0E0'; e.target.style.backgroundColor = '#F9F9F9'; e.target.style.boxShadow = 'none'; }}
                />
            </div>
        </div>
    );

    return (
        <div style={{ fontFamily: '"Outfit", sans-serif', maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '45px' }}>
                <div>
                    <h1 style={{ margin: '0 0 10px 0', fontSize: '32px', fontWeight: '800', color: '#2C1A10', letterSpacing: '-1px' }}>
                        Finance Governance
                    </h1>
                    <p style={{ margin: 0, color: '#999', fontSize: '16px', fontWeight: '500' }}>
                        Secure management of legislative dues and financial audits.
                    </p>
                </div>
            </div>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                title="Synchronize Payment Entry"
            >
                <form onSubmit={handleFormSubmit} style={{ animation: 'fadeIn 0.4s ease' }}>
                    <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.6', marginBottom: '30px' }}>
                        Record offline legislative payments into the secure registry. All entries are subject to audit clearance.
                    </p>

                    <InputField
                        label="Member Identity ID"
                        value={formData.memberId}
                        onChange={(val) => setFormData({ ...formData, memberId: val })}
                        placeholder="e.g. MLA-PAT-001"
                        required
                    />

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <InputField
                            label="Amount (INR)"
                            type="number"
                            icon={ICONS.rupee}
                            value={formData.amount}
                            onChange={(val) => setFormData({ ...formData, amount: val })}
                            placeholder="0.00"
                            required
                        />
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>
                                Payment Method *
                            </label>
                            <select
                                value={formData.method}
                                onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    borderRadius: '12px',
                                    border: '1px solid #E0E0E0',
                                    fontSize: '14px',
                                    color: '#2C1A10',
                                    backgroundColor: '#F9F9F9',
                                    outline: 'none',
                                    cursor: 'pointer',
                                    height: '52px'
                                }}
                            >
                                <option value="CASH">Liquid Cash</option>
                                <option value="CHEQUE">Bank Cheque</option>
                                <option value="DRAFT">Demand Draft</option>
                            </select>
                        </div>
                    </div>

                    <InputField
                        label="Reference Number"
                        value={formData.ref}
                        onChange={(val) => setFormData({ ...formData, ref: val })}
                        placeholder="Draft/Cheque/Receipt No."
                        required
                    />

                    <button
                        type="submit"
                        style={{
                            width: '100%',
                            padding: '18px',
                            backgroundColor: '#C0722C',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px',
                            fontWeight: '800',
                            fontSize: '16px',
                            cursor: 'pointer',
                            marginTop: '20px',
                            boxShadow: '0 10px 20px rgba(192, 114, 44, 0.2)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2C1A10'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#C0722C'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        Confirm Identity & Record Payment
                    </button>
                </form>
            </SlidingDrawer>

            <SlidingDrawer
                isOpen={isDetailDrawerOpen}
                onClose={() => setIsDetailDrawerOpen(false)}
                title="Member Ledger Detail"
            >
                {selectedDue && (() => {
                    const designation = selectedDue.designation || selectedDue.memberId?.designation || 'LEGISLATIVE MEMBER';
                    const officialId = selectedDue.officialId || selectedDue.memberId?.officialId || 'N/A';
                    const email = selectedDue.memberEmail || selectedDue.email || selectedDue.memberId?.email || 'N/A';
                    const phone = selectedDue.memberPhone || selectedDue.mobile || selectedDue.memberId?.mobile || 'N/A';
                    const reason = selectedDue.reason || 'Outstanding legislative membership & administrative utility dues';
                    const rawNoc = selectedDue.nocStatus || selectedDue.noc || selectedDue.memberId?.nocStatus;
                    const nocVal = rawNoc ? String(rawNoc).toUpperCase() : 'PENDING';
                    const isActiveNoc = nocVal === 'ACTIVE' || nocVal === 'CLEARED' || nocVal === 'APPROVED';

                    return (
                        <div style={{ animation: 'fadeIn 0.4s ease' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #F0E8DB' }}>
                                <div style={{ width: '80px', height: '80px', backgroundColor: '#FDF8F3', borderRadius: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', marginBottom: '15px' }}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                </div>
                                <h3 style={{ margin: '0 0 5px 0', fontSize: '22px', fontWeight: '800', color: '#2C1A10', textAlign: 'center' }}>
                                    {selectedDue.memberName}
                                </h3>
                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    {designation}
                                </span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>Official Identity ID</label>
                                    <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>{officialId}</div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>Email Address</label>
                                    <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>{email}</div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>Mobile Number</label>
                                    <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>{phone}</div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>Outstanding Balance</label>
                                    <div style={{ fontSize: '24px', color: '#D32F2F', fontWeight: '900' }}>₹{selectedDue.totalDue?.toLocaleString()}</div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>Reason for Dues</label>
                                    <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', backgroundColor: '#FDF8F3', padding: '15px', borderRadius: '12px', border: '1px solid #F0E8DB' }}>
                                        {reason}
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>NOC Control Status</label>
                                    <div>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: isActiveNoc ? '#ECFDF5' : '#FEF2F2', borderRadius: '14px', border: `1px solid ${isActiveNoc ? '#10B98130' : '#EF444430'}` }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isActiveNoc ? '#10B981' : '#D32F2F' }} />
                                            <span style={{ fontSize: '12px', fontWeight: '900', color: isActiveNoc ? '#10B981' : '#D32F2F', textTransform: 'uppercase' }}>
                                                {nocVal}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {selectedDue.lastPaymentDate && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>Last Synchronization</label>
                                        <div style={{ fontSize: '14px', color: '#555', fontWeight: '600' }}>
                                            {new Date(selectedDue.lastPaymentDate).toLocaleString()} (recorded amount: ₹{selectedDue.lastPaymentAmount?.toLocaleString()})
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </SlidingDrawer>

            <div style={{ background: '#fff', borderRadius: '32px', border: '1px solid #F0E8DB', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.03)', minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '25px 40px', background: '#FDF8F3', borderBottom: '1px solid #F0E8DB' }}>
                    <h3 style={{ fontSize: '13px', color: '#C0722C', margin: 0, textTransform: 'uppercase', fontWeight: '900', letterSpacing: '1.5px' }}>Outstanding Dues Ledger</h3>
                </div>

                {loading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', fontWeight: '700' }}>Synchronizing Financial Data...</div>
                ) : (
                    dues.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 40px', textAlign: 'center' }}>
                            <div style={{ width: '100px', height: '100px', backgroundColor: '#FDF8F3', borderRadius: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', marginBottom: '25px' }}>
                                {ICONS.ledger}
                            </div>
                            <h3 style={{ margin: '0 0 10px 0', fontSize: '22px', fontWeight: '800', color: '#2C1A10' }}>No Outstanding Dues Detected</h3>
                            <p style={{ margin: 0, color: '#999', fontSize: '16px', fontWeight: '500', maxWidth: '400px' }}>All member financial records are synchronized with the central legislative treasury.</p>
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#FDF8F3' }}>
                                <tr>
                                    <th style={{ padding: '20px 40px', textAlign: 'left', fontSize: '12px', fontWeight: '900', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Official Identity</th>
                                    <th style={{ padding: '20px 40px', textAlign: 'left', fontSize: '12px', fontWeight: '900', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Outstanding Balance</th>
                                    <th style={{ padding: '20px 40px', textAlign: 'left', fontSize: '12px', fontWeight: '900', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Last Synchronization</th>
                                    <th style={{ padding: '20px 40px', textAlign: 'left', fontSize: '12px', fontWeight: '900', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Ledger Status</th>
                                    <th style={{ padding: '20px 40px', textAlign: 'right', fontSize: '12px', fontWeight: '900', color: '#999', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dues.map((due, idx) => (
                                    <tr key={idx} style={{ borderBottom: idx === dues.length - 1 ? 'none' : '1px solid #FDF8F3', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = '#F9F9F9'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '25px 40px', fontSize: '16px', fontWeight: '800', color: '#2C1A10' }}>{due.memberName || 'Unknown Member'}</td>
                                        <td style={{ padding: '25px 40px', fontSize: '18px', fontWeight: '900', color: '#D32F2F' }}>₹{due.totalDue.toLocaleString()}</td>
                                        <td style={{ padding: '25px 40px', fontSize: '14px', color: '#888', fontWeight: '500' }}>{due.lastPaymentDate ? new Date(due.lastPaymentDate).toLocaleDateString() : 'Initial Record'}</td>
                                        <td style={{ padding: '25px 40px', textAlign: 'left' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#FEF2F2', borderRadius: '14px' }}>
                                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D32F2F' }} />
                                                <span style={{ fontSize: '11px', fontWeight: '900', color: '#D32F2F', textTransform: 'uppercase' }}>Clearance Pending</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 40px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => {
                                                    setSelectedDue(due);
                                                    setIsDetailDrawerOpen(true);
                                                }}
                                                style={{
                                                    padding: '10px 20px',
                                                    backgroundColor: '#FDF8F3',
                                                    border: '1px solid #C0722C40',
                                                    borderRadius: '12px',
                                                    color: '#C0722C',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    fontSize: '13px'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#C0722C'; e.currentTarget.style.color = '#fff'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FDF8F3'; e.currentTarget.style.color = '#C0722C'; }}
                                            >
                                                View Detail
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
            </div>
        </div>
    );
};

export default FinanceManager;
